import { teacherMessage, teacherChecks, checkSummary, catalogText } from './teacher-language.js';
import { ShareLibraryBuild } from './library-share.js';
import { ResourceLink } from './resources.js';
import { ActivityArt } from './activity-art.js';
import { readTeacherDraft, writeTeacherDraft } from './teacher-drafts.js';
import { MaterialsSurface, type MaterialsPreviewProps } from './materials.js';
import type { ResourceView } from './api.js';
/**
 * TIP Shell — "Build an app" (Teacher Console surface; trusted chrome, parent DOM only).
 *
 * The teacher describes an activity in plain language; the platform writes it, runs it
 * through the governed ship pipeline (doc 06), and — only if every gate passes — offers
 * ONE action: publish it. PURE presentation: the gates, their order, their decisions are the SERVER's. This file explains those decisions for teachers
 * without changing them. Original evidence stays available in support details.
 *
 * Design (DESIGN.md, Paper & Seal): describe → the gates settle in one at a time → the
 * record of what was checked. The serif carries the gate sentences and the outcome
 * statement (they are on-the-record lines a district reads); seal-red appears only on
 * publish — the moment something becomes real — and on a gate that STOPPED a build.
 *
 * Honest waiting: POST /teacher/build is synchronous and can run for minutes, and the
 * server has no progress channel. So the wait shows an indeterminate state and an
 * elapsed clock — never a fabricated percentage, never a fake stage-by-stage crawl.
 * The staggered reveal happens AFTER the verdicts are in hand, and collapses to a
 * single frame when the viewer asks for reduced motion.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { CAP_PHRASES } from './ui.js';
import { BuildRefused, buildMayStillBeRunning } from './api.js';
import type {
  ServerClient, BuildResult, BuildStage, BuildRedaction, PublishResult,
  ClarifyQuestion, TemplateChoice, SchoolLook,
} from './api.js';
import { loadConfig } from './config.js';
import { startTestRun, type TestRunVerdict, type TestRunHandle } from './testrun.js';
import type { Tier } from '../shared/index.js';

/** Gap between gate rows as they settle in. Long enough to read, short enough to trust. */
const STAGGER_MS = 250;

/** Viewer asked for less motion ⇒ every gate lands in one frame (no staggered timers). */
function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The two ways into a build. Chosen on the console's landing, switchable on the build screen. */
export type BuildPath = 'starter' | 'describe';

/**
 * The first thing on the console: ADD AN APP, and the two ways to do it, side by side.
 *
 * WHY TWO DOORS AND NOT ONE (Steve, 2026-09-30). This used to be a single "Build an app"
 * block whose only button said "Describe one →" — and that button ALSO led to the ten
 * ready-made apps. A teacher reading the landing had no way to know a finished app was one
 * click away, and "describe" is the harder of the two jobs. So the choice a teacher is
 * actually making — use one of ours, or make my own — is now the first thing they see, in
 * those words, as two equal buttons.
 *
 * NOT seal-red: the classes screen spends its one seal-red action on releasing grades, and
 * choosing a starting point puts nothing on the record (publishing, later, is the act).
 */
export function AddAppChoice({ onChoose, compact = false }: {
  onChoose(path: BuildPath): void;
  /** Inside the setup checklist: the step supplies the heading, so show only the two doors. */
  compact?: boolean;
}) {
  return (
    <section class={compact ? 'add-app compact' : 'add-app'} aria-labelledby={compact ? undefined : 'add-app-title'}>
      {compact ? null : (
        <>
          <h2 class="section-title" id="add-app-title">Create an activity for your class</h2>
          <p class="lede">
            Start with something ready to teach, or bring a new idea. Make it yours,
            try it out, then publish to your class.
          </p>
        </>
      )}
      <div class="add-app-choices">
        <button type="button" class="choice-card" onClick={() => onChoose('starter')}>
          {compact ? null : <ActivityArt slug="match-pairs" />}
          <span class="t">Start with a ready-made app</span>
          <span class="d">
            Pick a ready-made game: matching, quizzes, sorting or races. Try
            it, then change it to your content.
          </span>
          <span class="go" aria-hidden="true">Choose an app →</span>
        </button>
        <button type="button" class="choice-card" onClick={() => onChoose('describe')}>
          {compact ? null : <ActivityArt slug="sentence-studio" />}
          <span class="t">Create one from my idea</span>
          <span class="d">
            Describe the activity in your own words. TIP writes it and checks it before any
            student sees it.
          </span>
          <span class="go" aria-hidden="true">Describe it →</span>
        </button>
      </div>
    </section>
  );
}

export function BuildSurface({ api, classes, onLeave, onPublished, path: startPath = 'describe', kind: initialKind = 'assignment', initialBuildId, onRemember, onBusyChange, onMyApps, draftScope, onPathChange }: {
  onPathChange?(path: BuildPath): void;
  draftScope?: string | undefined;
  kind?: 'assignment'|'resource';
  initialBuildId?: string | undefined;
  onRemember?(id?: string): void;
  onBusyChange?(busy: boolean): void;
  onMyApps?(): void;
  api: ServerClient;
  /** Which door the teacher came in by. They can switch on this screen; the typed
   *  description survives the switch either way. */
  path?: BuildPath;
  /** The classes this teacher teaches — the console's OWN overview, passed down rather
   *  than re-fetched. Publishing needs one: it creates the assignment scores land on. */
  classes: { classId: string; name: string; link?: { provider: string; externalClassId: string } | null; school?: SchoolLook | null }[];
  /** Back to the class list (top of the same console — no navigation). */
  onLeave(): void;
  /** A publish landed: the console re-reads its overview and the record. */
  onPublished(): void;
}) {
  const descriptionSlot = `new:${initialKind}`;
  const descriptionContext = useRef(initialBuildId ? `describe:${initialBuildId}` : descriptionSlot);
  // One idempotency key per Build/Change press (migration 0025). Kept only while a failed
  // press may have left that build running server-side, and only for the SAME inputs: the
  // next press then reattaches to the original build instead of starting a second one.
  const pendingKey = useRef<{ sig: string; key: string | undefined } | null>(null);
  const keyFor = (sig: string): string | undefined => {
    if (pendingKey.current?.sig !== sig) {
      pendingKey.current = { sig, key: (globalThis.crypto as Crypto | undefined)?.randomUUID?.() };
    }
    return pendingKey.current.key;
  };
  const settleKey = (err?: unknown) => { if (err === undefined || !buildMayStillBeRunning(err)) pendingKey.current = null; };
  const [description, setDescription] = useState(() => initialBuildId ? '' : readTeacherDraft(draftScope, descriptionSlot));
  const [draftSaved, setDraftSaved] = useState(() => !initialBuildId && !!readTeacherDraft(draftScope, descriptionSlot));
  const setDescriptionDraft = (text: string) => {
    setDescription(text);
    setDraftSaved(writeTeacherDraft(draftScope, descriptionContext.current, text));
  };
  const [tier, setTier] = useState<Tier>('standard');
  const [busy, setBusy] = useState<string | null>(null); // one action at a time
  const [build, setBuild] = useState<BuildResult | null>(null);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [materialsDraftId, setMaterialsDraftId] = useState<string | undefined>(() => readTeacherDraft(draftScope, 'materials:flip-card-drill') || undefined);
  const kind = build?.kind ?? initialKind;
  const [targets,setTargets]=useState<ResourceView[]|null>(null);
  const [resourceAppId,setResourceAppId]=useState<string|null>(null);
  const [targetRevision,setTargetRevision]=useState(0);
  useEffect(()=>{
    if(kind!=='resource'||!build?.id)return;
    let active=true;setTargets(null);
    Promise.all([api.resources(),api.teacherBuild(build.id)]).then(([r,b])=>{if(active){setTargets(r.resources);setResourceAppId(b.resourceAppId??null);}}).catch(()=>{if(active)setTargets(null);});
    return ()=>{active=false;};
  },[api,kind,build?.id,targetRevision]);
  const currentBuildId = useRef<string | null>(null);
  currentBuildId.current = build?.id ?? null;
  const previewReport = useRef(0);
  const [refusal, setRefusal] = useState<{ message: string; blocked: boolean } | null>(null);
  const [revealed, setRevealed] = useState(0); // gate rows shown so far
  const [published, setPublished] = useState<PublishResult | null>(null);
  const [publishMsg, setPublishMsg] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loadRevision, setLoadRevision] = useState(0);
  const [failedChange, setFailedChange] = useState<BuildResult | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [classId, setClassId] = useState(''); // where a passed build gets published
  const statusRef = useRef<HTMLDivElement>(null);
  // The test run: what the app actually did when the teacher ran it. Reset on every new
  // build, because a verdict belongs to the exact source it ran — the server enforces
  // the same rule by hash, so a stale verdict here could never buy a publish anyway.
  const [tested, setTested] = useState<TestRunVerdict | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);
  const [refineText, setRefineText] = useState('');
  const [refineSaved, setRefineSaved] = useState(false);
  const setRefineDraft = (text: string) => {
    setRefineText(text);
    setRefineSaved(!!build && writeTeacherDraft(draftScope, `edit:${build.id}`, text));
  };
  // The clarifying interview. ONE round: `questions` is set once per description and is
  // never re-fetched from an answer, by construction — there is no code path that calls
  // doClarify() from within the question card.
  const [questions, setQuestions] = useState<ClarifyQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [clarifyNote, setClarifyNote] = useState('');
  const [nothingToAsk, setNothingToAsk] = useState(false);
  // What is being asked for RIGHT NOW, held separately from the box.
  // The box is cleared when a refinement starts (so a teacher does not stare at text
  // they already sent), but the wait screen still has to quote the sentence back —
  // reading the emptied box printed “” and told them nothing about what was running.
  const [asked, setAsked] = useState('');
  // The starter pack, and which one is picked ('' ⇒ the teacher is describing their own).
  // Non-blocking, like the build history: an older server with no /teacher/templates
  // route, or any failure, simply means no cards — the describe box is untouched.
  const [templates, setTemplates] = useState<TemplateChoice[]>([]);
  const [chosen, setChosen] = useState('');
  const [path, setPath] = useState<BuildPath>(startPath);
  const changePath = (next: BuildPath) => { setPath(next); onPathChange?.(next); };
  // No pack to offer (an older server, or the fetch failed) ⇒ there is only one door, and
  // the screen must never strand a teacher on an empty "pick one" list.
  const onStarter = path === 'starter' && templates.length > 0;
  const toDescribe = () => { setChosen(''); changePath('describe'); };

  // One class ⇒ there is nothing to choose. Preselect it and show no picker at all,
  // so the passed build keeps a single uncluttered action.
  useEffect(() => {
    if (classes.length === 1) setClassId(classes[0]!.classId);
  }, [classes]);

  useEffect(() => {
    onBusyChange?.(busy !== null);
  }, [busy, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  useLayoutEffect(() => {
    if (build) onRemember?.(build.id);
  }, [build?.id, onRemember]);

  const restoreBuild = (saved: BuildResult) => {
    setBuild(saved);
    descriptionContext.current = `describe:${saved.id}`;
    const descriptionDraft = readTeacherDraft(draftScope, `describe:${saved.id}`);
    setDescription(saved.parentBuildId ? '' : descriptionDraft || saved.prompt);
    setDraftSaved(!!descriptionDraft);
    const draft = readTeacherDraft(draftScope, `edit:${saved.id}`);
    setRefineText(draft); setRefineSaved(!!draft);
    if ((saved.status === 'failed' || saved.status === 'interrupted') && !saved.parentBuildId) setPath('describe');
    setTested(saved.previewStatus ? {
      status: saved.previewStatus,
      headline: saved.previewStatus === 'ran_clean' ? 'The previous test run finished without reported errors.'
        : 'The previous test run needs attention. Run it again before publishing.',
      errors: [], report: null,
    } : null);
  };
  useEffect(() => {
    if (!initialBuildId) return;
    let active = true;
    setBusy('open'); setLoadError(false);
    api.teacherBuild(initialBuildId).then(saved => {
      if (active) restoreBuild(saved);
    }).catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setBusy(null); });
    return () => { active = false; };
  }, [api, initialBuildId, loadRevision]);

  // Reopening an in-progress request reads its durable record; it never starts a new build.
  useEffect(() => {
    if (!build || !['generating', 'gating'].includes(build.status)) return;
    let active = true;
    const id = setInterval(() => {
      api.teacherBuild(build.id).then(saved => {
        if (active) { restoreBuild(saved); setLoadError(false); }
      }).catch(() => { if (active) setLoadError(true); });
    }, 5000);
    return () => { active = false; clearInterval(id); };
  }, [api, build?.id, build?.status]);

  useEffect(() => {
    api.teacherTemplates(kind).then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
  }, [api,kind]);

  // A name-blind school (the pilot default) has already decided the tier for every app, so
  // the choice below would be ignored by the server anyway — say that instead of offering
  // it. A failed fetch keeps the choice; the server still builds at the school's tier.
  const [schoolTier, setSchoolTier] = useState<Tier | null>(null);
  useEffect(() => {
    Promise.resolve().then(() => api.teacherBuildSettings()).then((r) => {
      setSchoolTier(r.districtTier);
      if (r.districtTier === 'high_assurance') setTier('high_assurance');
    }).catch(() => setSchoolTier(null));
  }, [api]);

  // The elapsed clock IS the liveness signal during a multi-minute build — it is the
  // one thing we can say that is true. aria-hidden: a ticking region would flood a
  // screen reader; the status sentence above it is what gets announced.
  useEffect(() => {
    if (busy !== 'build' && busy !== 'refine' && busy !== 'starter') return;
    const startedAt = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);

  // Keep focus alive across the wait: the "Build it" button unmounts the moment the
  // build starts, which would drop a keyboard user on <body> for several minutes.
  useEffect(() => {
    if (busy === 'build' || busy === 'refine' || busy === 'starter') statusRef.current?.focus();
  }, [busy]);

  // The staggered reveal. Verdicts are already decided and already on the server's
  // record; this only paces how they are READ.
  useEffect(() => {
    if (build === null) return;
    const n = build.stages.length;
    if (prefersReducedMotion()) {
      setRevealed(n);
      return;
    }
    setRevealed(0);
    const timers = build.stages.map((_, i) => setTimeout(() => setRevealed(i + 1), STAGGER_MS * i));
    return () => { for (const t of timers) clearTimeout(t); };
  }, [build]);

  /** Everything that belongs to ONE version of the app. A new build or a refinement
   *  invalidates all of it — most importantly the test-run verdict. */
  const clearPerBuildState = () => {
    currentBuildId.current = null;
    previewReport.current++;
    setRefusal(null);
    setFailedChange(null);
    setPublished(null);
    setPublishMsg(null);
    setRevealed(0);
    setTested(null);
    setTestErr(null);
    setRefineText('');
  };

  /**
   * "Ask me a few questions first." ONE round — see server/harness/prompt.ts for why this
   * must never become a loop. No questions back (a clear description, or a provider
   * hiccup) is not a failure: it means there is nothing to ask, so say so and let them
   * build. Only a BLOCKED prompt surfaces a refusal, and that is the redactor talking.
   */
  const doClarify = async () => {
    const text = description.trim();
    if (text.length === 0) return;
    setBusy('clarify');
    setRefusal(null);
    setClarifyNote('');
    try {
      const r = await api.clarifyBuild(text);
      setQuestions(r.questions);
      setAnswers({});
      setNothingToAsk(r.questions.length === 0);
    } catch (e) {
      setRefusal(
        e instanceof BuildRefused
          ? { message: e.message, blocked: e.blocked }
          : { message: e instanceof Error ? e.message : String(e), blocked: false },
      );
    }
    setBusy(null);
  };

  const doBuild = async () => {
    const text = description.trim();
    if (text.length === 0) return;
    setBusy('build');
    setBuild(null);
    clearPerBuildState();
    setAsked(text);
    // Answers ride along; the SERVER folds them into the description, upstream of
    // redaction. Nothing is composed in the browser.
    const clarify = questions.length > 0
      ? { questions, answers, note: clarifyNote.trim() || undefined }
      : undefined;
    const requestKey = keyFor(JSON.stringify(['build', text, tier, kind, clarify ?? null]));
    try {
      const result = await api.buildApp(text, tier, clarify, kind, requestKey);
      settleKey();
      writeTeacherDraft(draftScope, descriptionContext.current, ''); setDraftSaved(false);
      descriptionContext.current = `describe:${result.id}`;
      setBuild(result);
    } catch (e) {
      settleKey(e);
      setRefusal(
        e instanceof BuildRefused
          ? { message: e.message, blocked: e.blocked }
          : { message: e instanceof Error ? e.message : String(e), blocked: false },
      );
    }
    setBusy(null);
  };

  /**
   * Build a starter app the teacher PICKED. Same route, same gates, same required test
   * run — the only difference is where the first draft came from.
   *
   * ⚠ The description is NOT sent, even if there is text in the box. These bytes are
   * pinned, so nothing typed could have changed them, and shipping the sentence anyway
   * would put a cause on the district's record that this build did not have. The server
   * enforces the same rule independently (build.ts `run`). Making it theirs is what the
   * "Want it different?" box does afterwards, and THAT turn is recorded, because it is
   * the one that actually drives the model.
   */
  const doStarter = async () => {
    const pick = templates.find((t) => t.slug === chosen);
    if (!pick) return;
    setBusy('starter');
    setBuild(null);
    clearPerBuildState();
    setAsked(pick.title);
    try {
      setBuild(await api.buildFromTemplate(pick.slug, tier, kind));
    } catch (e) {
      setRefusal(
        e instanceof BuildRefused
          ? { message: e.message, blocked: e.blocked }
          : { message: e instanceof Error ? e.message : String(e), blocked: false },
      );
    }
    setBusy(null);
  };

  /** "Make it harder." The server makes a NEW build whose parent is this one and runs it
   *  through the same six gates; the live app (if any) keeps serving the old version. */
  const doRefine = async () => {
    const text = refineText.trim();
    if (text.length === 0 || text.length > 1000 || build === null) return;
    setBusy('refine');
    const previous = build;
    const previousTested = tested;
    const previousTestErr = testErr === 'Saving your test run…'
      ? 'That test run was still being saved when you started editing. Run it again before publishing.'
      : testErr;
    const previousPublished = published;
    setBuild(null);
    clearPerBuildState();
    setAsked(text);
    const requestKey = keyFor(JSON.stringify(['refine', previous.id, text]));
    try {
      const changed = await api.refineBuild(previous.id, text, requestKey);
      settleKey();
      if (changed.status === 'failed' || changed.status === 'interrupted') {
        setBuild(previous); setTested(previousTested); setTestErr(previousTestErr);
        setPublished(previousPublished); setRefineText(text); setFailedChange(changed);
      } else {
        writeTeacherDraft(draftScope, `edit:${previous.id}`, ''); setRefineSaved(false);
        setBuild(changed);
      }
    } catch (e) {
      settleKey(e);
      // Put the build back — the teacher has not lost the app they were working on.
      setBuild(previous); setTested(previousTested); setTestErr(previousTestErr); setPublished(previousPublished);
      setRefineText(text);
      setRefusal(
        e instanceof BuildRefused
          ? { message: e.message, blocked: e.blocked }
          : { message: e instanceof Error ? e.message : String(e), blocked: false },
      );
    }
    setBusy(null);
  };

  /** What the frame reported, recorded server-side. A failure to record is surfaced and
   *  leaves the publish blocked — never silently treated as "tested". */
  const onTested = async (buildId: string, v: TestRunVerdict) => {
    if (currentBuildId.current !== buildId) return;
    const report = ++previewReport.current;
    // Only confirmed evidence may be restored after a failed refinement. While this
    // write is pending the previous verdict stays visible and publication stays blocked.
    setTestErr('Saving your test run…');
    try {
      await api.reportTestRun(buildId, v.status, v.errors.join('\n'), v.artifactSha256);
      if (currentBuildId.current === buildId && previewReport.current === report) {
        setTested(v); setTestErr(null);
      }
    } catch (e) {
      if (currentBuildId.current !== buildId || previewReport.current !== report) return;
      setTestErr(
        e instanceof BuildRefused
          ? e.message
          : `The test run could not be recorded: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  const doPublish = async (buildId: string) => {
    if (classId === '') return;
    const into = classes.find((c) => c.classId === classId)?.name ?? 'your class';
    setBusy('publish');
    try {
      if(kind==='resource'&&targets===null) throw new Error('Resource version details could not load. Refresh before publishing.');
      const target=targets?.find(t=>t.classId===classId&&t.appId===resourceAppId);
      const r = await api.publishBuild(buildId, classId, target?.revision);
      setPublished(r);
      setPublishMsg(r.libraryPinStatus==='pending'
        ? 'Published to your class. Library details are still updating.'
        : r.kind==='resource'
        ? r.state==='available' ? `“${r.name}” is available as a resource for ${into}. Copy the class link below.`
          : `“${r.name}” was saved for ${into}. Access remains ${r.state}. You can make it available in Resources.`
        : `Published as “${r.name}” and assigned to ${into}.`);
      setTargetRevision(n=>n+1);
      onPublished();
    } catch (e) {
      // Keep definite server refusals distinct from an unconfirmed publish response.
      setPublishMsg(
        e instanceof BuildRefused && e.status !== undefined && e.status >= 400 && e.status < 500
          ? `⛔ ${e.message}`
          : '⛔ TIP could not confirm publication. Check My apps before trying again.',
      );
    }
    setBusy(null);
  };

  const startOver = () => {
    onRemember?.();
    descriptionContext.current = `new:${kind}`;
    setBuild(null);
    clearPerBuildState();
  };

  const building = busy === 'build' || busy === 'refine' || busy === 'starter';
  const failed = build !== null && (build.status === 'failed' || build.status === 'interrupted');
  const inProgress = build?.status === 'generating' || build?.status === 'gating';
  // The form comes back UNDER a failed build, description intact: the reasons the gates
  // gave and the box you edit belong on the same screen.
  const showForm = !building && busy !== 'open' && !loadError && (build === null || (failed && !build?.parentBuildId && !build?.source));
  const allRevealed = build !== null && revealed >= build.stages.length;

  if (materialsOpen) return <MaterialsSurface
    api={api}
    tier={tier}
    initialDraftId={materialsDraftId}
    onLeave={() => {
      setMaterialsOpen(false);
      if (!build) return;
      // Editing content may have revoked the approval shown by the parent screen.
      // Clear it before the read, so even a slow/failed refresh cannot reuse it.
      const previous = { ...build }; delete previous.materialsApproval;
      setBuild(previous); setBusy('open'); setLoadError(false);
      void api.teacherBuild(build.id).then(refreshed => {
        if (build.materialDraftId && !refreshed.materialDraftId) refreshed.materialDraftId = build.materialDraftId;
        restoreBuild(refreshed);
      }).catch(() => setLoadError(true)).finally(() => setBusy(null));
    }}
    onBusyChange={onBusyChange}
    onRemember={id => {
      setMaterialsDraftId(id);
      // Only a server-owned opaque ID is tab-local. Curriculum text stays on the server.
      writeTeacherDraft(draftScope, 'materials:flip-card-drill', id ?? '');
    }}
    onApproved={next => {
      clearPerBuildState(); setBuild(next); setMaterialsOpen(false);
      writeTeacherDraft(draftScope, 'materials:flip-card-drill', '');
      setMaterialsDraftId(undefined);
    }}
    renderPreview={previewProps => <MaterialsBuildPreview {...previewProps} api={api} />}
  />;

  const tierChoice = (kind==='resource' || onStarter && chosen === '' ? null : schoolTier === 'high_assurance' ? (
            <div class="tier-choice">
              <p class="tier-fixed">
                <strong>Student names stay with TIP.</strong> Your school does not let apps receive
                student names from TIP. TIP can still show names beside the activity.
              </p>
            </div>
            ) : (
            <fieldset class="tier-choice">
              <legend>Student names in this app</legend>
              <label class="tier-opt">
                <input
                  type="radio"
                  name="tier"
                  value="standard"
                  checked={tier === 'standard'}
                  disabled={busy !== null}
                  onChange={() => setTier('standard')}
                />
                <span class="t">Allow first names</span>
                <span class="d">
                  The app may receive and show first names when your school allows it.
                </span>
              </label>
              <label class="tier-opt">
                <input
                  type="radio"
                  name="tier"
                  value="high_assurance"
                  checked={tier === 'high_assurance'}
                  disabled={busy !== null}
                  onChange={() => setTier('high_assurance')}
                />
                <span class="t">Keep names with TIP</span>
                <span class="d">
                  TIP does not send student names to the app. TIP can show names beside it.
                  Scores and typed answers still work. Students should not type personal information.
                </span>
              </label>
            </fieldset>
            ));

  return (
    <section>
      {kind==='resource'?<p class="resource-help">Create a class tool students can use anytime. They open a link, with no scores or work to turn in. What they type is not saved when they close the page.</p>:null}
      <div class="build-head">
        <h2 class="section-title">
          {kind==='resource' ? 'Your class resource' : build || initialBuildId ? 'Your saved app' : onStarter ? 'Start with a ready-made app' : 'Create an app from your idea'}
        </h2>
        <span class="push">
          {onMyApps ? <button type="button" class="quiet" disabled={busy !== null} onClick={onMyApps}>← My apps</button> : null}
          <button type="button" class="quiet" disabled={busy !== null} onClick={onLeave}>
            ← Your classes
          </button>
        </span>
      </div>

      <ol class="build-steps" aria-label="App creation steps">
        <li class={build ? 'complete' : 'current'} aria-current={!build ? 'step' : undefined}><span aria-hidden="true">1</span> Choose or describe</li>
        <li class={build && !published && build.status !== 'published' ? 'current' : ''} aria-current={build && !published && build.status !== 'published' ? 'step' : undefined}><span aria-hidden="true">2</span> Test & customize</li>
        <li class={published || build?.status === 'published' ? 'complete' : ''}><span aria-hidden="true">3</span> Publish to class</li>
      </ol>

      {busy === 'open' ? <p role="status">Opening your saved app…</p> : null}
      {loadError ? <div class="notice-line" role="alert">This saved app could not load.
        <button type="button" class="quiet" onClick={() => setLoadRevision(n => n + 1)}>Try again</button>
        {onMyApps ? <button type="button" class="quiet" onClick={onMyApps}>Back to My apps</button> : null}
      </div> : null}
      {refusal ? <Refusal refusal={refusal} /> : null}
      {failedChange ? <div class="notice-line" role="alert">
        <strong>That change did not finish. Your previous version is still here.</strong>
        <p>{teacherMessage(failedChange.failureReason, 'The changed version did not pass its checks. Review the notes below.')}</p>
        <p>Your instructions are still in the box below. You can edit them or try again.</p>
        <IntentReviewReport build={failedChange} />
        {failedChange.stages.length ? <details><summary>See the checks for this attempt</summary>
          {failedChange.stages.map(s => <GateRow key={s.stage} stage={s} />)}</details> : null}
      </div> : null}

      {/* ONE live region for the whole surface: it is always in the DOM, so a screen
          reader hears the wait begin and hears the outcome. The gate rows below are
          static content to be read, not six separate announcements.
          It is also the focus anchor — pressing "Build it" unmounts the button that
          had focus, so focus moves HERE (a stable element that survives the wait) and
          Tab afterwards continues into the result. */}
      <div ref={statusRef} tabIndex={-1} aria-live="polite">
        {building ? (
          <div class="building">
            <p class="working">
              {busy === 'refine'
                ? 'Making that change…'
                : busy === 'starter' ? 'Checking it…' : 'Writing the app…'}
            </p>
            {/* THE DURATION IS DIFFERENT AND MUST BE SAID DIFFERENTLY. A described build
                waits on a model for one to three minutes; a starter app is already
                written, so the only work is the gates and it lands in seconds. Telling a
                teacher "a few minutes" and finishing in four would be a small lie that
                costs the next estimate its credibility. */}
            <p class="working-sub">
              {busy === 'refine'
                ? 'TIP checks the changed app again. Your students keep using the current version until you publish the new one. This takes a few minutes. Keep this page open.'
                : busy === 'starter'
                  ? 'TIP is checking this ready-made activity before you try it. This usually takes a few seconds.'
                  : 'TIP creates and checks the app, then you try it before sharing it with students. This takes a few minutes. Keep this page open.'}
            </p>
            <p class="asked record-voice">“{asked}”</p>
            <p class="elapsed" aria-hidden="true">{fmtElapsed(elapsed)} elapsed</p>
          </div>
        ) : null}
        {build !== null ? <Outcome build={build} /> : null}
      </div>

      {build !== null ? (
        <>
          <IntentReviewReport build={build} />
          {kind === 'assignment' && (build.materialDraftId || build.model === 'tip/template@flip-card-drill') ? <div class="materials-build-entry">
            <button type="button" class="step" disabled={busy !== null} onClick={() => {
              setMaterialsDraftId(build.materialDraftId); setMaterialsOpen(true);
            }}>{build.materialDraftId ? 'Review class content' : 'Use my class materials'}</button>
            <p class="compose-hint">Upload or paste your material to make a new Flip Card Drill with editable terms and answers.</p>
          </div> : null}
          <details class="build-checks" open={failed}>
            <summary>{failed ? 'Review what needs attention' : 'View safety checks'}<span>{build.stages.filter(s => s.verdict === 'pass').length} checks passed</span></summary>
          <div class="gates">
            {build.stages.slice(0, revealed).map((s) => <GateRow key={s.stage} stage={s} />)}
          </div>
          </details>

          {allRevealed && (build.status === 'passed' || build.status === 'published' || (failed && !!build.source)) ? (
            <PassedBuild
            resourceTarget={targets?.find(t=>t.classId===classId&&t.appId===resourceAppId)}
            targetsLoaded={kind!=='resource'||targets!==null}
              api={api}
              build={build}
              busy={busy}
              classes={classes}
              classId={classId}
              setClassId={setClassId}
              published={published}
              publishMsg={publishMsg}
              tested={tested}
              testErr={testErr}
              onTested={onTested}
              refineText={refineText}
              setRefineText={setRefineDraft}
              refineSaved={refineSaved}
              onRefine={() => void doRefine()}
              onPublish={() => void doPublish(build.id)}
              onAnother={startOver}
              onLeave={onLeave}
            />
          ) : null}

          {(failed || inProgress) && build.parentBuildId ? <div class="notice-line">
            <p>Your change request: {build.prompt}</p>
            <button type="button" class="step" disabled={busy !== null} onClick={async () => {
              const failed = build;
              setBusy('open'); setRefusal(null);
              try {
                const parent = await api.teacherBuild(failed.parentBuildId!);
                clearPerBuildState(); restoreBuild(parent);
                setRefineText(failed.prompt);
                // The recovered request belongs to the parent now being edited. The
                // current render still names the failed child, so persist explicitly.
                setRefineSaved(writeTeacherDraft(draftScope, `edit:${parent.id}`, failed.prompt));
                setFailedChange(failed);
              } catch { setRefusal({ message: 'The previous version could not load. You can find it in My apps.', blocked: false }); }
              setBusy(null);
            }}>Resume previous version</button>
          </div> : null}
          {inProgress ? <div>
            <p class="lede">TIP is still working on this app. This page updates automatically and will tell you if it stops.</p>
            {!build.parentBuildId ? <button type="button" class="step" onClick={() => {
              const text = build.prompt;
              startOver(); changePath('describe'); setDescriptionDraft(text);
            }}>Use this request in a new draft</button> : null}
          </div> : null}
          {allRevealed ? <Redactions redactions={build.redactions} /> : null}
        </>
      ) : null}

      {showForm ? (
        <div class={build === null ? '' : 'section-gap'}>
          <div class={`compose${onStarter ? ' library-compose' : ''}`}>
            {onStarter ? (
              <StarterPack
                templates={templates}
                chosen={chosen}
                disabled={busy !== null}
                onChoose={setChosen}
                onDescribe={toDescribe}
                selectedControls={<>{tierChoice}<StarterAction
                pick={templates.find((t) => t.slug === chosen)}
                busy={busy}
                typed={description.trim().length > 0}
                onBuild={() => void doStarter()}
                onMaterials={kind === 'assignment' && chosen === 'flip-card-drill' ? () => setMaterialsOpen(true) : undefined}
                hasMaterialsDraft={!!materialsDraftId}
              /></>}
              />
            ) : (
            <>
            {templates.length > 0 ? (
              <p class="switch-path">
                <button type="button" class="linkish" disabled={busy !== null} onClick={() => changePath('starter')}>
                  Or start with a ready-made app →
                </button>
              </p>
            ) : null}
            <label class="compose-label" for="build-desc">{kind==='resource'?'Describe the resource you want.':'Describe the activity you want.'}</label>
            <textarea
              id="build-desc"
              class="field compose-box"
              rows={5}
              placeholder={kind==='resource'?'A unit converter for physics, with worked steps and metric units.':'A Pythagorean warm-up themed to our school, with a class leaderboard.'}
              value={description}
              disabled={busy !== null}
              onInput={(e) => {
                setDescriptionDraft((e.currentTarget as HTMLTextAreaElement).value);
                // Questions belong to the description they were asked about. Editing the
                // description invalidates them, exactly as a new build invalidates a
                // test-run verdict — stale answers folded into different words would
                // build something nobody asked for.
                if (questions.length > 0) { setQuestions([]); setAnswers({}); setClarifyNote(''); }
                setNothingToAsk(false);
              }}
            />
            <p class="draft-note">{description && draftSaved ? 'Draft saved in this tab. You can refresh and keep writing.' : 'Describe the topic, students’ level, and how you want them to practice.'}</p>

            {nothingToAsk ? (
              <p class="clarify-none settle">
                TIP has enough information to start. Choose
                <strong> Create my app</strong>.
              </p>
            ) : null}

            {questions.length > 0 ? (
              <ClarifyCard
                questions={questions}
                answers={answers}
                note={clarifyNote}
                disabled={busy !== null}
                onAnswer={(id, option) =>
                  setAnswers((m) => (m[id] === option
                    // Tapping the chosen option again clears it — a teacher who changes
                    // their mind should not be stuck with an answer they cannot unpick.
                    ? Object.fromEntries(Object.entries(m).filter(([k]) => k !== id))
                    : { ...m, [id]: option }))
                }
                onNote={setClarifyNote}
              />
            ) : null}
            </>
            )}

            {!onStarter ? tierChoice : null}

            {/* TWO DOORS TO THE SAME BUILD.
                The wording here is load-bearing and was rewritten after a review found
                the obvious labels ("Just make it" / "Let's chat to make it the PERFECT
                app") unusable: the second implies the first produces a worse app, so a
                teacher with 25 minutes of prep stalls, afraid of picking wrong. These
                labels say what each does, not which is better, and the note underneath
                answers the question that actually blocks the press — what does choosing
                cost me? (Nothing: the questions are optional and skippable.) */}
            {!onStarter ? (
              <>
                <div class="compose-act">
                  <button
                    type="button"
                    class="act"
                    disabled={busy !== null || description.trim().length === 0}
                    onClick={() => void doBuild()}
                  >
                    {failed ? 'Try again' : 'Create my app'}
                  </button>
                  <button
                    type="button"
                    class="step"
                    disabled={busy !== null || description.trim().length === 0}
                    hidden={kind==='resource'}
                    onClick={() => void doClarify()}
                  >
                    {busy === 'clarify' ? 'Thinking…' : 'Ask me a few questions first'}
                  </button>
                </div>
                <p class="compose-hint">
                  Not sure what to include? TIP can ask a few questions to help you describe your idea.
                  You can skip any question.
                </p>
              </>
            ) : null}

            <p class="record-voice i compose-note">
              Leave out student names and other personal information. TIP handles names
              according to your school’s settings.
            </p>
          </div>
        </div>
      ) : null}

    </section>
  );
}

/**
 * THE STARTER PACK — the apps TIP wrote, one of the two doors on the landing.
 *
 * WHAT THIS IS FOR. The hardest screen in the product is an empty textarea reading
 * "describe the activity you want". A teacher with 25 minutes of prep who cannot think of
 * a sentence builds nothing at all, and "nothing at all" is the outcome the platform is
 * competing against — not a rival product. Ten things that already work, that they can
 * open, run and then change, is a different question entirely: not "what do I want?" but
 * "which of these is closest?"
 *
 * ⚠ WHY IT IS A RADIO GROUP AND NOT A ROW OF CARDS THAT BUILD ON CLICK. Two reasons, and
 * the second is the one that decided it. (1) Native radios in a fieldset are the accessible
 * answer to "one of these, or none" for free — arrow-key traversal, a group name, a
 * legend — and `.tier-opt` on this same screen already uses exactly that shape. (2) Build
 * on click would spend a teacher's build allowance on a mis-click, and it would skip the
 * tier choice below, which applies to a starter app just as much as to a described one.
 *
 * The way out to describing is a plain button under the grid, not a radio in it (changed
 * 2026-09-30, when the landing split into two doors). As the last radio it read as a sixth
 * app; a teacher who came in by "ready-made" should see only apps, plus one clear exit.
 *
 * Renders nothing at all when the list is empty — an older server without the templates
 * route, or any failure fetching it, leaves the describe box exactly as it was.
 */
function StarterPack(props: {
  templates: TemplateChoice[];
  chosen: string;
  disabled: boolean;
  onChoose(slug: string): void;
  /** Leave the pack for the describe box (the other door on the landing). */
  onDescribe(): void;
  selectedControls: ComponentChildren;
}) {
  const [query, setQuery] = useState('');
  const selection = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // Reveal the next step without moving keyboard focus off the chosen radio.
    selection.current?.querySelector<HTMLButtonElement>('.starter-act button')?.scrollIntoView?.({block:'nearest',inline:'nearest',behavior:'instant'});
  }, [props.chosen]);
  const needle = query.trim().toLocaleLowerCase();
  const matches = props.templates.filter(t => [t.title,t.blurb,t.carries,t.compareTo,t.comparison].some(v => v?.toLocaleLowerCase().includes(needle)));
  if (props.templates.length === 0) return null;
  return (
    <fieldset class="starter">
      <legend>Pick the app closest to your lesson</legend>
      <p class="starter-lede">
        Choose the activity that fits your lesson. Each comes with sample content you
        can change after opening it.
      </p>

      <div class="library-tools">
        <div><label for="activity-search">Find an activity</label>
          <input id="activity-search" type="search" class="field" value={query} disabled={props.disabled}
            placeholder="Try quizzes, matching, science, or a familiar tool…" onInput={e => setQuery(e.currentTarget.value)} />
        </div>
        <span class="library-count" role="status">{matches.length} of {props.templates.length} activities</span>
        {query ? <button type="button" class="quiet" onClick={() => setQuery('')}>Clear search</button> : null}
      </div>
      {props.chosen && !matches.some(t => t.slug === props.chosen) ? <p class="draft-note" role="status">Selected: {props.templates.find(t => t.slug === props.chosen)?.title}. Clear search to see it.</p> : null}
      {matches.length === 0 ? <p class="workspace-empty">No activities match “{query}”. Try another topic or clear your search.</p> : null}

      <div class="starter-grid">
        {matches.map((t) => {
          const { mechanic, subject } = splitTitle(t.title);
          return (
            <div class={`starter-option${props.chosen === t.slug ? ' on' : ''}`} key={t.slug}>
            <label class={`starter-card${props.chosen === t.slug ? ' on' : ''}`}>
              <input
                type="radio"
                name="starter"
                value={t.slug}
                checked={props.chosen === t.slug}
                disabled={props.disabled}
                onChange={() => props.onChoose(t.slug)}
              />
              <ActivityArt slug={t.slug} />
              <span class="t">{mechanic}</span>
              {t.compareTo && t.comparison ? (
                <span class="compare">Compare to {t.compareTo}: {catalogText(t.comparison)}.</span>
              ) : null}
              <span class="d">{catalogText(t.blurb)}</span>
              {/* The two facts a teacher needs to choose: what it ARRIVES with, and what
                  it can be turned into. Without the first they are surprised by a
                  chemistry deck; without the second they cannot tell whether their own
                  material fits the mechanic at all. */}
              {subject ? <span class="c">Comes with {subject}</span> : null}
              <span class="c">Works with {catalogText(t.carries)}</span>
            </label>
            {props.chosen === t.slug ? <div class="starter-selection" ref={selection}>{props.selectedControls}</div> : null}
            </div>
          );
        })}
      </div>

      {props.templates.some((t) => t.compareTo && t.comparison) ? (
        <p class="starter-comparison-note">
          Comparisons describe specific activities, not entire products. Product names
          belong to their respective owners. TIP is not affiliated with or endorsed by them.
        </p>
      ) : null}

      {props.chosen === '' ? (
        <p class="compose-hint">Pick one above to see what you will get.</p>
      ) : null}
      <p class="switch-path">
        <button type="button" class="linkish" disabled={props.disabled} onClick={props.onDescribe}>
          None of these fit? Create one from my idea →
        </button>
      </p>
    </fieldset>
  );
}

/**
 * The one action for a picked starter app, and the sentence that sets the expectation.
 *
 * The expectation is the whole job here. A teacher who presses this thinking they are
 * about to get a fractions game about THEIR fractions, and receives element symbols, has
 * been misled by the screen — so the panel says what arrives, and says the change comes
 * after. That ordering is not a limitation to apologise for: the app they get is one that
 * has already been run, reviewed and gated, and changing it from there is a far better
 * starting point than a blank page.
 */
function StarterAction(props: {
  pick: TemplateChoice | undefined;
  busy: string | null;
  /** There is text in the describe box. It is NOT being sent — say so plainly. */
  typed: boolean;
  onBuild(): void;
  onMaterials?: (() => void) | undefined;
  hasMaterialsDraft?: boolean;
}) {
  const { pick } = props;
  if (!pick) return null;
  const { subject } = splitTitle(pick.title);
  return (
    <div class="starter-act">
      <div class="compose-act">
        {props.onMaterials ? <button type="button" class="act" disabled={props.busy !== null} onClick={props.onMaterials}>{props.hasMaterialsDraft ? 'Resume my class materials' : 'Use my class materials'}</button> : null}
        <button
          type="button"
          class={props.onMaterials ? 'step' : 'act'}
          disabled={props.busy !== null}
          onClick={props.onBuild}
        >
          {props.busy === 'starter' ? 'Checking it…' : props.onMaterials ? 'Try the sample content' : 'Next'}
        </button>
      </div>
      {props.onMaterials ? <p class="compose-hint">Bring a PDF, Word document, or pasted class material. Review the proposed cards as a list or by playing, then approve your version.</p> : <p class="compose-hint">
        Open the sample activity{subject ? ` with ${subject}` : ''}. Then use{' '}
        <strong>Want it different?</strong> to adapt it to your lesson.
      </p>}
      {props.typed ? (
        <p class="compose-hint starter-typed">
          Your earlier description will not change this ready-made activity. Open it first,
          then use “Want it different?” to request changes. Your description is still saved under
          <strong> Create one from my idea</strong>.
        </p>
      ) : null}
    </div>
  );
}

/**
 * "Match Pairs — Element Symbols" → the mechanic and the deck it ships with.
 *
 * Both halves are load-bearing and they are used in different places: the MECHANIC names
 * the button and the card, because that is what the teacher is choosing; the SUBJECT is
 * what warns them the app arrives full of somebody else's content. A title with no em
 * dash yields no subject rather than a guess.
 */
function splitTitle(title: string): { mechanic: string; subject: string | null } {
  const at = title.indexOf('—');
  if (at < 0) return { mechanic: title.trim(), subject: null };
  const mechanic = title.slice(0, at).trim();
  const subject = title.slice(at + 1).trim();
  return { mechanic: mechanic || title.trim(), subject: subject || null };
}

/**
 * THE CLARIFYING INTERVIEW — the questions a teacher did not know to answer.
 *
 * The problem this solves, in the founder's words: a teacher types "make a graphing game,
 * engaging and funny" and what comes back is nothing like what was in their head — because
 * straight-lines-or-curves, what grade, and whether square roots belong were never in the
 * prompt. They are not going to think of any of that. So TIP asks.
 *
 * The questions are GENERATED from the description, not a fixed form: "straight lines or
 * curves?" is a question that only exists for a graphing app, and a static grade/difficulty
 * form could never ask it.
 *
 * ⚠ Every answer is a TAP, not typing. That is a privacy property, not a style choice —
 * the strings that go back to the model are ones the platform already had, so the common
 * path adds no new teacher free-text heading to a third party. The single optional note
 * below is the one exception and rides the same redactor as the description.
 *
 * Every question is skippable, and skipping means "you choose" rather than "ask me again".
 * There is no path from this card back to the interview: one round, by construction.
 */
function ClarifyCard(props: {
  questions: ClarifyQuestion[];
  answers: Record<string, string>;
  note: string;
  disabled: boolean;
  onAnswer(id: string, option: string): void;
  onNote(v: string): void;
}) {
  const answered = props.questions.filter((q) => props.answers[q.id] !== undefined).length;
  return (
    <div class="clarify settle">
      <div class="clarify-head">
        <h3 class="minor-head">A few things TIP does not know yet</h3>
        <span class="clarify-count">
          {answered} of {props.questions.length} answered · skip any of them
        </span>
      </div>

      {props.questions.map((q) => (
        <fieldset class="clarify-q" key={q.id}>
          <legend>{q.ask}</legend>
          <div class="clarify-opts">
            {q.options.map((o) => {
              const on = props.answers[q.id] === o;
              return (
                <button
                  type="button"
                  key={o}
                  class={`chip${on ? ' on' : ''}`}
                  aria-pressed={on ? 'true' : 'false'}
                  disabled={props.disabled}
                  onClick={() => props.onAnswer(q.id, o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}

      <label class="clarify-note-label" for="clarify-note">
        Anything else it should know? <span class="opt">(optional)</span>
      </label>
      <textarea
        id="clarify-note"
        class="field clarify-note"
        rows={2}
        maxLength={500}
        placeholder="It's for my 3rd period, right after the quiz on slope."
        value={props.note}
        disabled={props.disabled}
        onInput={(e) => props.onNote((e.currentTarget as HTMLTextAreaElement).value)}
      />
      <p class="clarify-foot">
        Answer what you like, then press <strong>Create my app</strong>. Anything you skip, TIP
        decides for you.
      </p>
    </div>
  );
}

/** A build the server would not run. A BLOCKED prompt is a governance moment (the
 *  description never left the district) — everything else is a flat failure line. */
function Refusal({ refusal }: { refusal: { message: string; blocked: boolean } }) {
  if (!refusal.blocked) {
    return <p class="notice-line" role="alert">TIP could not confirm this step: {teacherMessage(refusal.message, refusal.blocked ? 'Remove personal information from your request, then try again.' : 'TIP could not confirm whether this step finished. Check My apps before starting again. If it keeps happening, use Report an issue.')}</p>;
  }
  return (
    <div class="stop-note settle" role="alert">
      <span><span class="dot seal" /><span class="state-seal">Not sent</span></span>
      <p>{teacherMessage(refusal.message, refusal.blocked ? 'Remove personal information from your request, then try again.' : 'TIP could not confirm whether this step finished. Check My apps before starting again. If it keeps happening, use Report an issue.')}</p>
      <p class="record-voice">Your request was not sent to the AI service. No new app was created.</p>
    </div>
  );
}

/** What was built and what the gates decided — the on-the-record sentence, in the record
 *  voice. A passed build is NAMED here, above its own verdicts: the teacher should know
 *  what the gates were checking before they read the checking. */
function Outcome({ build }: { build: BuildResult }) {
  const n = build.stages.length;
  const passed = build.stages.filter((s) => s.verdict === 'pass').length;
  const stopped = build.stages.filter((s) => s.verdict === 'fail').length;
  const skipped = n - passed - stopped;

  if (build.status === 'generating' || build.status === 'gating') return <p role="status">{build.title || 'Your app'}: {build.status === 'generating' ? 'creating' : 'checking'}…</p>;
  if (build.status === 'interrupted') return <><p class="verdict-line record-voice">TIP stopped before this app was ready. Nothing was published.</p>{build.failureReason ? <p class="fail-reason">{teacherMessage(build.failureReason, 'TIP could not finish this version. Review the checks below, then edit your request or try again.')}</p> : null}</>;
  if (build.status === 'published') return <><h3 class="app-title lead">{build.title || 'Your app'}</h3><p class="verdict-line record-voice">This version was published. Changes create a new draft for you to test before publishing again.</p></>;
  if (build.status === 'passed') {
    return (
      <>
        <h3 class="app-title lead">{build.title ?? 'Your app'}</h3>

        <p class="verdict-line record-voice">
          {n} {n === 1 ? 'check' : 'checks'} reviewed it.{' '}
          {skipped > 0 ? `${passed} passed, ${skipped} were not completed` : 'Every one passed'}.
          Students cannot open it until you publish it (make it available to your class).
        </p>
      </>
    );
  }
  return (
    <>
      <p class="verdict-line record-voice">
        {n === 0 ? 'TIP could not finish creating this app. Your request is saved.'
          : stopped > 0 ? `${stopped} of ${n} checks found problems with this app.` : 'TIP could not finish correcting this app.'}
        {' '}Nothing new was published.
      </p>
      {build.failureReason ? <p class="fail-reason">{teacherMessage(build.failureReason, 'TIP could not finish this version. Review the checks below, then edit your request or try again.')}</p> : null}
    </>
  );
}

/** One gate: what it is, what it checks, what it decided, and why. */
/** Source-bound AI observations stay visible beside the exact teacher preview. */
export function IntentReviewReport({build}: {build: BuildResult}) {
  const findings=build.stages.flatMap(stage=>stage.findings).filter(f=>f.rule.startsWith('intent_'));
  const summary=findings.find(f=>f.rule==='intent_summary_v1');
  if(!summary)return null;
  const concerns=findings.filter(f=>f.rule==='intent_concern_v1'||f.rule==='intent_platform_failure_v1');
  const incomplete=findings.filter(f=>f.rule==='intent_uncovered_v1');
  const assessed=findings.filter(f=>f.rule==='intent_assessed_v1');
  return <section class="intent-review" aria-label="Automated app review">
    <h3>Automated app review</h3>
    <p>{teacherMessage(summary.message, 'The AI review could not finish. Try the activity yourself and check it against your request.')}</p>
    <p class="intent-review-note">Based on your request. These are AI observations, so your preview is the final check.</p>
    {concerns.length?<div><h4>Needs your attention</h4><ul>{concerns.map((f,i)=><li key={i}>{teacherMessage(f.message.replace(/^Concern: /,''), 'The AI found a possible problem. Review the check details below and try this part of the activity.')}</li>)}</ul></div>:null}
    {incomplete.length?<details><summary>Still to review ({incomplete.length})</summary><ul>{incomplete.map((f,i)=><li key={i}>{teacherMessage(f.message.replace(/^Not completed: /,''), 'The AI could not check this part. Try the activity yourself.')}</li>)}</ul></details>:null}
    {assessed.length?<details><summary>What the AI tried ({assessed.length})</summary><ul>{assessed.map((f,i)=><li key={i}>{teacherMessage(f.message.replace(/^AI assessment: /,''), 'The AI tried part of the activity. Your own review is still needed.')}</li>)}</ul></details>:null}
  </section>;
}

function GateRow({ stage }: { stage: BuildStage }) {
  return (
    <div class="gate-row settle">
      <span class="label">{teacherChecks[stage.stage]?.label ?? 'Additional check'}</span>
      <span class="verdict">
        {stage.verdict === 'pass' ? (
          <><span class="dot ok" /><span class="state-ok">Passed</span></>
        ) : stage.verdict === 'fail' ? (
          <><span class="dot seal" /><span class="state-seal">Stopped it</span></>
        ) : (
          <span class="skipped">Not completed</span>
        )}
      </span>
      <span class="desc">{teacherChecks[stage.stage]?.description ?? 'Review the result and any notes below.'}</span>
      <p class="summary">{checkSummary(stage)}</p>
      {stage.findings.filter(f=>!f.rule.startsWith('intent_')).map((f, i) => (
        <p class={`finding${f.severity === 'reject' ? ' reject' : ''}`} key={`${f.rule}-${i}`}>
          <span class="rule">{f.severity === 'reject' ? 'Needs a fix' : 'Review note'}</span>
          <span class="msg">{teacherMessage(f.message, f.severity === 'reject' ? 'TIP found a problem with this part of the app. Ask TIP to fix it in the change box below.' : 'This check has a note for review. See the support details below.')}</span>
        </p>
      ))}
      <details class="check-support"><summary>Details for technical support</summary>
        <p>These are the original check results. You do not need to understand the codes. You can copy them into a problem report or a request to fix the app.</p>
        <p>{stage.label}: {stage.summary}</p>
        {stage.findings.map((f, i) => <p key={i}><code>{f.rule}{f.line === undefined ? '' : ` (code line ${f.line})`}</code>: {f.message}</p>)}
      </details>
    </div>
  );
}

/** The passed build: what it is, what it may do, what it DOES when you run it, the change
 *  box, and the one action. The ordering is the product: gates → run it → change it →
 *  publish. Publishing is deliberately last and deliberately blocked until it has run. */
function PassedBuild(props: {
  resourceTarget?: ResourceView | undefined;
  targetsLoaded?: boolean;
  api: ServerClient;
  build: BuildResult;
  busy: string | null;
  classes: { classId: string; name: string; link?: { provider: string; externalClassId: string } | null; school?: SchoolLook | null }[];
  classId: string;
  setClassId(id: string): void;
  published: PublishResult | null;
  publishMsg: string | null;
  tested: TestRunVerdict | null;
  testErr: string | null;
  onTested(buildId: string, v: TestRunVerdict): void;
  refineText: string;
  refineSaved: boolean;
  setRefineText(v: string): void;
  onRefine(): void;
  onPublish(): void;
  onAnother(): void;
  onLeave(): void;
}) {
  const { api, build, busy, classes, classId, published, publishMsg, tested, testErr } = props;
  const source = build.source ?? '';
  const chosen = classes.find((c) => c.classId === classId);
  // The server refuses a publish with no recorded test run; the button reflects that
  // rather than letting a teacher press it and read a refusal.
  const isPublished = published !== null || build.status === 'published';
  const canTest = build.status === 'passed' || build.status === 'published';
  const approvedMaterials = !!build.materialsApproval && !!build.materialDraftId && build.materialsApproval.draftId === build.materialDraftId;
  const materialBuild = !!build.materialDraftId || build.provider === 'tip-class-materials';
  const mayPublish = (build.status === 'passed' || build.kind==='resource'&&build.status==='published') && (materialBuild ? approvedMaterials : tested !== null && testErr === null) && props.targetsLoaded!==false && (build.kind!=='resource'||tested?.status==='ran_clean');

  return (
    <div class="settle">
      {build.parentBuildId ? (
        <p class="lineage">
          This is a new version of your app. TIP keeps the earlier version
          and its test results.
        </p>
      ) : null}



      {approvedMaterials ? <p class="record-voice"><span class="dot seal" />You approved the terms and answers in version {build.materialsApproval!.revision}. This game passed its automatic checks.</p> : null}
      {canTest ? (
        <TestRunStage
          key={build.id}
          api={api}
          buildId={build.id}
          kind={build.kind??'assignment'}
          verdict={tested}
          recordError={testErr}
          disabled={busy !== null}
          onVerdict={(v) => props.onTested(build.id, v)}
          // The class it is headed for, else any class of theirs with a school: a test run
          // should look like the launch will.
          school={(chosen ?? classes.find((c) => c.school))?.school ?? null}
        />
      ) : null}

      <details class="app-permissions">
        <summary>App permissions & privacy</summary>
      <div class="ledger-line">
        <span class="k">What this app can do</span>
        <span class="v">{build.kind==='resource'?'A reusable tool. It does not read names, record scores or save typed values.':capabilitySentence(build.capabilities)}</span>
      </div>
      {build.tier === 'high_assurance' && build.kind!=='resource' ? (
        <div class="ledger-line">
          <span class="k">Names</span>
          <span class="v">
            TIP does not send student names to this app. TIP can still show names beside it.
          </span>
        </div>
      ) : null}
      </details>

      {
        <div class="refine">
          <label class="refine-label" for="refine-box">Want it different?</label>
          <textarea
            id="refine-box"
            class="field refine-box"
            rows={3}
            maxLength={1000}
            aria-describedby="refine-help refine-count"
            placeholder="Use six photosynthesis questions for grade 7. Make the answer buttons larger and dark green."
            value={props.refineText}
            disabled={busy !== null}
            onInput={(e) => props.setRefineText((e.currentTarget as HTMLTextAreaElement).value)}
          />
          <div class="input-meta">
            <p id="refine-help" class="draft-note">{props.refineText && props.refineSaved ? 'Draft saved in this tab.' : 'A few specific changes in one paragraph work best.'}</p>
            <span id="refine-count" class={props.refineText.length > 1000 ? 'state-warn' : ''}>{props.refineText.length.toLocaleString()} / 1,000 characters</span>
          </div>
          <details class="edit-examples"><summary>See examples</summary>
            <ul>
              <li><strong>Content:</strong> Replace the questions with eight questions about photosynthesis for grade 7. Include the correct answers.</li>
              <li><strong>Difficulty:</strong> Use two-step equations with whole-number answers. Keep the number of questions the same.</li>
              <li><strong>Appearance:</strong> Make the answer buttons larger and dark green, with white text.</li>
              <li><strong>A fix:</strong> After the last question, show the final score and a working “Play again” button.</li>
            </ul>
          </details>
          <div class="refine-act">
            {/* `.step`, not `.act`, and the call was close. A refinement DOES create a
                chained build that is gated and recorded — by the seal-red rule it has a
                claim. But `Publish to my class` is on this same screen, and Paper & Seal
                allows exactly one seal-red per screen: two would teach the teacher that
                red means nothing. Publishing is the moment here; changing is the step. */}
            <button
              type="button"
              class="step"
              disabled={busy !== null || props.refineText.trim().length === 0 || props.refineText.length > 1000}
              onClick={props.onRefine}
            >
              {busy === 'refine' ? 'Changing…' : 'Make that change'}
            </button>
            <span class="refine-hint">
              Your published version keeps running. Test each new version before publishing it.
            </span>
          </div>
        </div>
      }

      {/* CLOSED by default. The panel itself stays — "here is exactly what will run"
          is part of the legibility argument and a district reviewer must be able to
          reach it in one click — but a teacher who wanted a fractions game should not
          have several hundred lines of JavaScript as the answer. The line count is in
          the summary so the reader knows what they are opening. */}
      {source === '' ? null : (
        <details class="source-wrap">
          <summary>
            <span>App code (optional technical details)</span>
            <span class="len">{source.split('\n').length} lines</span>
          </summary>
          {/* Focusable so a keyboard-only reader can scroll it (WCAG 2.1 AA). */}
          <p>You do not need to read or edit this code. It is available for technical support.</p>
          <pre class="source" tabIndex={0} role="region" aria-label="App code for technical support, scrollable">
            <code>{source}</code>
          </pre>
        </details>
      )}

        <details class="build-provenance"><summary>How this app was made</summary><p>These tool names help support identify how TIP created this version.</p><p class="provenance">
          Creation tool: {build.model}. Service: {build.provider}.
          {build.attempts > 1 ? ` · ${build.attempts} attempts before it passed` : ''}
        </p></details>
      {publishMsg ? (
        publishMsg.startsWith('⛔') ? (
          <div class="action-result settle err">{teacherMessage(publishMsg, 'TIP could not confirm publication. Open My apps to check this version before trying again.')}</div>
        ) : (
          // The one confirmation that IS a record moment — it carries the seal.
          <div class="action-result settle sealed"><span class="dot seal" />{publishMsg}</div>
        )
      ) : null}

      {published?.installationId?<ResourceLink id={published.installationId}/>:null}
      {build.kind==='resource'&&props.resourceTarget?<p class="resource-update-note">{props.resourceTarget.className} already has a published version. Publishing replaces the version for this class. Other classes keep their current versions.</p>:null}
      {isPublished && build.kind!=='resource' ? (
        <div class="ledger-line" style="border-bottom:0;">
          <button type="button" class="step" onClick={props.onAnother}>Create another app</button>
          <button type="button" class="quiet" onClick={props.onLeave}>← Your classes</button>
        </div>
      ) : canTest ? (
        <div class="publish-row">
          {classes.length > 1 ? (
            <>
              <label class="publish-label" for="publish-class">Class</label>
              <select
                id="publish-class"
                class="field"
                value={classId}
                disabled={busy !== null}
                onChange={(e) => props.setClassId((e.currentTarget as HTMLSelectElement).value)}
              >
                <option value="" disabled>Choose the class this app is for…</option>
                {classes.map((c) => (
                  <option key={c.classId} value={c.classId}>{c.name}</option>
                ))}
              </select>
            </>
          ) : null}
          <button
            type="button"
            class="act"
            disabled={busy !== null || classId === '' || !mayPublish}
            title={
              classes.length === 0
                ? 'Ask your school’s TIP administrator to add your class first'
                : !mayPublish
                  ? materialBuild ? 'Review and approve the class content first' : 'Run the app first'
                  : ''
            }
            onClick={props.onPublish}
          >
            {busy === 'publish'
              ? 'Publishing…'
              : build.supersedes ? 'Publish this version' : 'Publish to my class'}
          </button>
          <span class="publish-note">
            {classes.length === 0
              ? 'Your app is saved privately. Ask your school’s TIP administrator to add your class so you can share it with students.'
              : !mayPublish
                ? materialBuild ? 'Review and approve your class content before publishing this version.' : 'Try the app first. TIP runs automatic checks; you still need to check the content and how it works.'
                : build.kind==='resource'
                  ? 'Publish this version to the selected class and get a reusable resource link.'
                : build.supersedes
                  ? 'This replaces the activity students use. Their assignment and saved scores stay the same.'
                  : chosen
                    ? `It becomes an assignment in ${chosen.name} that students can open.` + (chosen.link === null
                      // Asked in context (TIP-Teacher-Onboarding-Research §patterns): the moment
                      // a teacher publishes into an unmatched class is the moment it matters.
                      ? ' That class is not connected to Google Classroom yet. Connect it from Your classes before sending grades.'
                      : '')
                    : 'It becomes an assignment in the class you choose.'}
          </span>
        </div>
      ) : <p class="lede">This version needs corrections before it can be tested or published.</p>}
      {canTest ? <ShareLibraryBuild key={build.id} api={api} buildId={build.id} title={build.title || 'Untitled app'} ready={tested?.status === 'ran_clean' && testErr === null} /> : null}
    </div>
  );
}

/**
 * THE TEST RUN — the teacher watches their own app run before a student can.
 *
 * The frame here is the SAME sealed opaque-origin iframe a student gets, from the same
 * sandbox origin, under the same CSP, talking the same port protocol. What differs is
 * who answers: a local stand-in (testrun.ts) with invented names and a leaderboard that
 * goes nowhere, so a draft can never touch a roster or write a score.
 *
 * The frame is mounted IMPERATIVELY into a plain container, outside Preact's controlled
 * subtree — the same rule as the student view (dom.ts): the seal's attributes are set in
 * one audited place and a re-render must never be able to rewrite them.
 *
 * FULL SCREEN (2026-07-25) — read this before touching it. A student sees the activity
 * full-bleed; the teacher approving it was seeing it in a 480px letterbox, which makes
 * the judgement we REQUIRE of them harder than the one the student will make. The fix
 * is deliberately parent-side: the Shell calls `requestFullscreen()` on a wrapper IT
 * owns and the sealed frame merely fills it. Do NOT "fix" this by adding
 * `allowfullscreen` / `allow="fullscreen"` to the iframe — that hands untrusted app
 * code a capability the seal (shell/seal.ts: sandbox="allow-scripts", allow="") exists
 * to withhold, and it would buy nothing this does not already give.
 */
function MaterialsBuildPreview(props: MaterialsPreviewProps & { api: ServerClient }) {
  const [verdict, setVerdict] = useState<TestRunVerdict | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  const recording = useRef(0);
  useEffect(() => () => { recording.current++; }, []);
  return <TestRunStage
    api={props.api}
    buildId={props.build.id}
    verdict={verdict}
    recordError={recordError}
    disabled={props.disabled}
    onVerdict={async value => {
      const turn = ++recording.current;
      props.onReady(false); setRecordError('Saving your test run…');
      try {
        await props.api.reportTestRun(props.build.id, value.status, value.errors.join('\n'), value.artifactSha256);
        if (turn !== recording.current) return;
        setVerdict(value); setRecordError(null); props.onReady(value.status === 'ran_clean');
      } catch (error) {
        if (turn !== recording.current) return;
        setRecordError(error instanceof Error ? error.message : 'Your test run could not be saved. Run it again.');
      }
    }}
  />;
}

function TestRunStage(props: {
  kind?: 'assignment'|'resource';
  api: ServerClient;
  buildId: string;
  verdict: TestRunVerdict | null;
  recordError: string | null;
  disabled: boolean;
  onVerdict(v: TestRunVerdict): void;
  /** The school look to run it in — the class it would be published to, when known. */
  school?: SchoolLook | null | undefined;
}) {
  const { verdict, recordError } = props;
  const stageRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<TestRunHandle | null>(null);
  const runGeneration = useRef(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // There is a frame in the stage right now. `running` goes false when the verdict
  // lands, but the app keeps playing — so expanding stays available afterwards.
  const [mounted, setMounted] = useState(false);
  // How the stage is enlarged right now. TWO mechanisms, deliberately:
  //   'native' — the Fullscreen API said yes. True full screen, no browser chrome.
  //   'cover'  — it said no, so the Shell covers the viewport in-page instead.
  // See `toggleExpand` for why the fallback is not optional.
  const [expand, setExpand] = useState<'none' | 'native' | 'cover'>('none');
  const expanded = expand !== 'none';

  // A frame must never outlive the surface that owns it.
  useEffect(() => () => { runGeneration.current++; handleRef.current?.stop(); }, []);

  // Follow the DOCUMENT, not our own click: Esc and the browser's own exit
  // affordance both end native fullscreen without going through the button.
  useEffect(() => {
    const sync = () => {
      if (document.fullscreenElement === wrapRef.current) setExpand('native');
      else setExpand((e) => (e === 'native' ? 'none' : e)); // never clobber 'cover'
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  // In the fallback, Esc is ours to honor — the browser is not managing an exit for
  // us. A teacher who has just been dropped into a full-window activity will press
  // Esc, and it must work there exactly as it does in real fullscreen.
  useLayoutEffect(() => {
    if (expand !== 'cover') return;
    const previousFocus = document.activeElement;
    const done = wrapRef.current?.querySelector<HTMLButtonElement>('button');
    done?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpand('none'); };
    const onFocus = (e: FocusEvent) => {
      if (e.target instanceof Node && !wrapRef.current?.contains(e.target)) done?.focus();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocus);
    // The page behind is hidden; letting it scroll under the cover is just confusing.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocus);
      document.body.style.overflow = prev;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [expand]);

  /**
   * Enlarge the test run, by whichever mechanism this browser permits.
   *
   * WHY THERE IS A FALLBACK AT ALL. `requestFullscreen()` can be refused for reasons
   * that have nothing to do with our code: a MANAGED CHROMEBOOK — the platform's
   * primary target device — can have fullscreen disabled by enterprise policy, a user
   * can block it per-site, and some embedded/automation contexts refuse it outright
   * (verified live on 2026-07-25: even `document.documentElement.requestFullscreen()`
   * from a real click returned `TypeError: not granted`). The failure mode of a bare
   * try/catch is the worst one available — the teacher presses a button and NOTHING
   * HAPPENS, with no way to tell a broken feature from a broken app.
   *
   * So a refusal falls back to covering the viewport from inside the page, which
   * requires no permission from anyone. Steve's ask was "full screen or near as
   * possible"; this is the "near as possible", and on a locked-down district device
   * it may be the only one available.
   */
  const toggleExpand = async () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (expand !== 'none') {
      if (document.fullscreenElement === wrap) { try { await document.exitFullscreen(); } catch { /* already out */ } }
      setExpand('none');
      return;
    }
    try {
      if (typeof wrap.requestFullscreen !== 'function') throw new Error('unsupported');
      await wrap.requestFullscreen({ navigationUI: 'hide' });
      // `fullscreenchange` sets 'native'; setting it here too keeps the label honest
      // if the event is slow.
      setExpand('native');
    } catch {
      setExpand('cover');
    }
  };

  const run = async () => {
    const stage = stageRef.current;
    if (!stage || running) return;
    const generation = ++runGeneration.current;
    const current = () => runGeneration.current === generation && stage.isConnected;
    handleRef.current?.stop();
    handleRef.current = null;
    stage.replaceChildren();
    setError(null);
    setNote(null);
    setRunning(true);
    try {
      const [cfg, grant] = await Promise.all([loadConfig(), props.api.testRunGrant(props.buildId)]);
      if (!current()) return;
      const handle = startTestRun({
        container: stage,
        sandboxOrigin: cfg.SANDBOX_ORIGIN,
        buildId: grant.buildId,
        draftToken: grant.draftToken,
        kind: grant.kind,
        tier: grant.tier,
        log: (m) => { if (current()) setNote(m); },
        theme: props.school
          ? { schoolName: props.school.name, mascot: props.school.mascot, primary: props.school.primary, accent: props.school.accent }
          : undefined,
      });
      handleRef.current = handle;
      setMounted(true);
      const verdict = await handle.verdict;
      if (!current()) { handle.stop(); return; }
      props.onVerdict({...verdict,...(grant.artifactSha256?{artifactSha256:grant.artifactSha256}:{})});
    } catch (e) {
      if (!current()) return;
      setError(
        e instanceof BuildRefused
          ? e.message
          : `The app could not be started: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    if (current()) setRunning(false);
  };

  return (
    <div class="testrun">
      <div class="testrun-head">
        <h3 class="minor-head">Try it yourself</h3>
        <span class="push">
          {mounted ? (
            <button type="button" class="step" onClick={() => void toggleExpand()}>
              {expanded ? 'Leave full screen' : 'Full screen'}
            </button>
          ) : null}
          <button type="button" class="step preview-launch" disabled={props.disabled || running} onClick={() => void run()}>
            {running ? 'Running…' : verdict ? 'Run it again' : 'Run the app'}
          </button>
        </span>
      </div>

      {/* The Shell's OWN element — this is what is enlarged, by either mechanism.
          The frame is a descendant and gains nothing from it. */}
      <div class={`testrun-wrap${expand === 'cover' ? ' cover' : ''}`} ref={wrapRef}>
        <div class="testrun-fsbar">
          <span>Student-sized preview. Names are made up and scores are not saved.</span>
          <span class="push">
            <button type="button" class="step" onClick={() => void toggleExpand()}>
              {expand === 'native' ? 'Done (Esc)' : 'Done'}
            </button>
          </span>
        </div>
        {/* Preact never renders into this node — startTestRun appends the sealed frame. */}
        <div class="testrun-stage" ref={stageRef} role="region" aria-label="Activity preview" />
      </div>

      <div aria-live="polite">
        {error ? <p class="notice-line" role="alert">{teacherMessage(error, 'The preview could not open. Try again. If it keeps happening, use Report an issue.')}</p> : null}
        {verdict ? (
          <>
            <p class="testrun-verdict">
              {verdict.status === 'ran_clean' ? (
                <span><span class="dot ok" /><span class="state-ok">Preview ran without errors</span></span>
              ) : (
                <span><span class="dot seal" /><span class="state-seal">Needs a change</span></span>
              )}
              <span class="headline">{verdict.headline}</span>
            </p>
            {verdict.errors.length > 0 ? (
              <details><summary>Error details for support or an app fix</summary><p>You can copy these details into the change box below. You do not need to understand the code.</p><ul class="testrun-errors">
                {verdict.errors.map((e, i) => <li key={`${i}-${e.slice(0, 24)}`}>{e}</li>)}
              </ul></details>
            ) : null}
            <p class="testrun-note">
              {verdict.status === 'ran_clean'
                ? props.kind==='resource'?'Try a few examples, including unusual or incorrect entries, before sharing this tool. What you type is not saved when you close the page.':'This is a practice run with made-up names. Scores are not saved. Publish when you are ready for students to use it.'
                : 'Describe what went wrong in the “Want it different?” box below. You can also copy the error details into that box.'}
            </p>
          </>
        ) : (
          <p class="testrun-note">
            {running
              ? teacherMessage(note, 'Opening your private preview…')
              : props.kind==='resource'?'Try the tool as students will use it. It must run without errors before you can share it with your class.':'Try the app here first. Names are made up and scores are not saved. You must try this version before sharing it with students.'}
          </p>
        )}
        {recordError ? <p class="notice-line" role="alert">{teacherMessage(recordError, 'TIP could not save the preview result. Try the preview again before publishing.')}</p> : null}
      </div>
    </div>
  );
}

/** What the platform stripped out of the description before it left the district.
 *  Rare — it renders only when the redactor actually did something. */
function Redactions({ redactions }: { redactions: BuildRedaction[] }) {
  if (redactions.length === 0) return null;
  return (
    <div class="section-gap">
      <h3 class="minor-head">Personal information found in your request</h3>
      {redactions.map((r, i) => (
        <div class="ledger-line" key={`${r.kind}-${i}`}>
          {r.action === 'blocked' ? (
            <span><span class="dot seal" /><span class="state-seal">{teacherMessage(r.message, 'TIP found possible personal information. Remove personal details before continuing.')}</span></span>
          ) : (
            <span style="color:var(--ink-soft);">{teacherMessage(r.message, 'TIP found possible personal information. Remove personal details before continuing.')}</span>
          )}
          <span class="push stamp">{r.action === 'redacted' ? 'Removed' : r.action === 'blocked' ? 'Not sent' : 'Please review'}</span>
        </div>
      ))}
    </div>
  );
}

/** Capability slugs as one sentence a teacher (and a board attorney) can read. */
function capabilitySentence(caps: string[]): string {
  if (caps.length === 0) {
    return 'No access to student data from TIP. Students should still avoid typing personal information into the activity.';
  }
  const clause = caps.map((c) => CAP_PHRASES[c] ?? c.replace(/_/g, ' ')).join(', ');
  return `${clause.charAt(0).toUpperCase()}${clause.slice(1)}. Nothing else.`;
}

/** m:ss — the honest thing to show when the server cannot tell us how far along it is. */
function fmtElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
